/**
 * PAPPO — ThemeSelector
 * Dropdown button with the theme options + color preview.
 * Closes on click-outside. Animated open/close.
 */

import { useState, useRef, useEffect } from 'react';
import { Palette } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { type ThemeName } from '../../themes/tokens';
import styles from './ThemeSelector.module.css';

const themeList: { key: ThemeName; label: string; emoji: string; colors: string[] }[] = [
  { key: 'light', label: 'Light', emoji: '☀️', colors: ['#4F46E5', '#F8FAFC', '#E2E8F0'] },
  { key: 'dark-graphite', label: 'Dark Graphite', emoji: '🌑', colors: ['#7C3AED', '#111315', '#2A2F35'] },
  { key: 'neon-purple', label: 'Neon Purple', emoji: '💜', colors: ['#A855F7', '#0D0D0F', '#1D1D25'] },
  { key: 'cyber', label: 'Deep Space Cyber', emoji: '🚀', colors: ['#A78BFA', '#020005', '#2E0854'] },
  { key: 'dark-red', label: 'Vermelho Escuro', emoji: '🩸', colors: ['#DC2626', '#0B0707', '#2A1C1C'] },
  { key: 'light-red', label: 'Vermelho Claro', emoji: '🌹', colors: ['#F87171', '#241619', '#4A3237'] },
  { key: 'ice-blue', label: 'Azul Gélido', emoji: '🧊', colors: ['#38BDF8', '#060A12', '#10233F'] },
];

export function ThemeSelector() {
  const { themeName, setTheme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  /* Close on click-outside */
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  return (
    <div className={styles.wrapper} ref={dropdownRef}>
      <button
        className={styles.trigger}
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Selecionar tema"
        title="Selecionar tema"
      >
        <Palette size={18} />
      </button>

      {isOpen && (
        <div className={styles.dropdown}>
          <div className={styles.dropdownHeader}>Tema</div>
          {themeList.map((t) => (
            <button
              key={t.key}
              className={`${styles.option} ${themeName === t.key ? styles.active : ''}`}
              onClick={() => {
                setTheme(t.key);
                setIsOpen(false);
              }}
            >
              <div className={styles.colorPreview}>
                {t.colors.map((c, i) => (
                  <span
                    key={i}
                    className={styles.colorDot}
                    style={{ backgroundColor: c }}
                  />
                ))}
              </div>
              <span className={styles.optionLabel}>
                {t.emoji} {t.label}
              </span>
              {themeName === t.key && (
                <span className={styles.checkmark}>✓</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
